import { Box, Flex, Grid, Heading } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { useSnackbar } from 'ui-components';
import { Creator } from '~/clientApi/creator.model';
import { AddTalentButton } from '~/components/ManagerTopBar/components/AddTalentButton';
import { useCreateMediaKit } from '~/hooks/api/media-kits/create';
import { MediaKit } from '~/hooks/api/media-kits/get';
import { useCreators } from '~/hooks/useCreators';

import CustomShareableCard from './CustomShareableCard';
import { CreateMediaKitButton } from './components/CreateMediaKitButton';
import { useSelectTalentStore } from './store/useSelectTalentStore';
import { getMediaKitPayload } from './utils';

export const AllShareableEmptyList = ({ creator }: { creator?: Creator }) => {
  const { setIsModalOpen } = useSelectTalentStore();
  const router = useRouter();
  const snackbar = useSnackbar();
  const createMediaKit = useCreateMediaKit();

  const response = useCreators({
    enableAgencyRoster: true,
  });

  const handleCreateMediaKit = () => {
    if (!creator) {
      setIsModalOpen(true);
      return;
    }
    // Create media kit for creator
    createMediaKit.mutate(getMediaKitPayload(creator), {
      onSuccess: (resp) => {
        if (resp.data?.id) {
          router.push(`/media-kits/${resp.data.id}/builder`);
          return;
        }
      },
      onError: (error: any) => {
        const errorMessage =
          error?.response?.data?.detail ||
          error?.message ||
          'There has been an error. Please try again';
        snackbar.error({
          description: errorMessage,
        });
      },
    });
  };

  // If we're filtering by a specific creator (both URL query and creator prop exist)
  const isFiltering = useMemo(
    () => router.query.creator && creator,
    [router.query.creator, creator],
  );

  const message = useMemo(() => {
    if (isFiltering) {
      return `No media kits found for ${creator?.fullName || 'this Talent'}`;
    }

    return response.creators?.length === 0
      ? 'Start by adding Talent to build your first pitch, rosters, media kits,\nand gain access to live content and social analytics.'
      : 'Create your first media kit for a Talent';
  }, [isFiltering, creator, response.creators?.length]);

  return (
    <Box
      position="relative"
      width="100%"
      minH="calc(100vh - 172px)"
      overflow="hidden"
    >
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        zIndex={1}
        pointerEvents="none"
      >
        <Grid
          templateColumns="repeat(auto-fill, minmax(300px, 1fr))"
          gap={4}
          justifyContent={{ base: 'center', lg: 'start' }}
        >
          {mockMediaKits.map((mockMediaKit) => (
            <CustomShareableCard key={mockMediaKit.id} {...mockMediaKit} />
          ))}
        </Grid>
      </Box>
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        background="linear-gradient(
          to bottom,
          rgba(255,255,255,0.5) 0%,
          rgba(255,255,255,0.8) 40%,  
          rgba(255,255,255,0.95) 60%, 
          rgba(255,255,255,1) 70%     
        )"
        zIndex={1}
      />

      <Flex
        direction="column"
        alignItems="center"
        gap={9}
        padding={6}
        zIndex={2}
        position="absolute"
        width="100%"
        bottom={20}
      >
        <Heading
          fontSize="3xl"
          fontWeight="medium"
          lineHeight={8}
          textAlign="center"
          whiteSpace="pre-line"
        >
          {message}
        </Heading>
        {response.creators?.length === 0 && !isFiltering ? (
          <AddTalentButton />
        ) : (
          <CreateMediaKitButton
            onClick={handleCreateMediaKit}
            trackId="create-new-media-kit-btn-empty-page"
            isDisabled={createMediaKit.isLoading}
          />
        )}
      </Flex>
    </Box>
  );
};

export const mockMediaKits: MediaKit[] = [
  {
    id: '38',
    created_at: '2070-08-04T00:55:27.841Z',
    updated_at: '2027-06-16T13:20:22.479Z',
    name: 'Sophie Brown Future Forward',
    agency_id: 'AgencyName',
    contact: {
      manager_email: 'blah@manager.com',
      manager_name: 'Sophia Martinez',
      user_id: '89',
    },
    created_by: {
      manager_email: 'blah@manager.com',
      manager_name: 'Sophia Martinez',
      user_id: '89',
    },
    blocks: [],
    thumbnail: '/images/media-kits/talent_profile.jpg',
    is_public: true,
  },
  {
    id: '27',
    created_at: '2025-04-13T15:32:41.401Z',
    updated_at: '2025-01-15T00:27:42.737Z',
    name: 'David Johnson Material 2025',
    agency_id: 'AgencyName',
    contact: {
      manager_email: 'blah@manager.com',
      manager_name: 'Sophia Martinez',
      user_id: '89',
    },
    created_by: {
      manager_email: 'blah@manager.com',
      manager_name: 'Daniel Taylor',
      user_id: '89',
    },
    blocks: [],
    thumbnail: '/images/media-kits/talent_profile2.jpg',
    is_public: false,
  },
  {
    id: '42',
    created_at: '2025-09-07T06:00:24.053Z',
    updated_at: '2087-03-08T05:13:52.236Z',
    name: 'John White Campaign Kit',
    agency_id: 'AgencyName',
    contact: {
      manager_email: 'blah@manager.com',
      manager_name: 'Sophia Martinez',
      user_id: '89',
    },
    created_by: {
      manager_email: 'blah@manager.com',
      manager_name: 'James Anderson',
      user_id: '89',
    },
    blocks: [],
    thumbnail: '/images/media-kits/talent_profile3.jpg',
    is_public: false,
  },
  {
    id: '2',
    created_at: '2025-06-14T21:10:37.137Z',
    updated_at: '2058-12-03T16:02:31.407Z',
    name: 'Ava Thompson Rise 2025',
    agency_id: 'AgencyName',
    contact: {
      manager_email: 'blah@manager.com',
      manager_name: 'Sophia Martinez',
      user_id: '89',
    },
    created_by: {
      manager_email: 'blah@manager.com',
      manager_name: 'Olivia Davis',
      user_id: '89',
    },
    blocks: [],
    thumbnail: '/images/media-kits/talent_profile4.jpg',
    is_public: false,
  },
  {
    id: '21',
    created_at: '2023-06-04T02:49:27.051Z',
    updated_at: '2023-08-22T21:49:02.408Z',
    name: 'Mia Wilson Progress Path 2022',
    agency_id: 'AgencyName',
    contact: {
      manager_email: 'blah@manager.com',
      manager_name: 'Sophia Martinez',
      user_id: '89',
    },
    created_by: {
      manager_email: 'blah@manager.com',
      manager_name: 'Ava Moore',
      user_id: '89',
    },
    blocks: [],
    thumbnail: '/images/media-kits/talent_profile5.jpg',
    is_public: false,
  },
  {
    id: '29',
    created_at: '2028-12-27T23:49:43.397Z',
    updated_at: '2077-09-04T20:16:23.092Z',
    name: 'Jose',
    agency_id: 'Michael Smith 2024 Kit',
    contact: {
      manager_email: 'blah@manager.com',
      manager_name: 'Sophia Martinez',
      user_id: '89',
    },
    created_by: {
      manager_email: 'blah@manager.com',
      manager_name: 'Scarlett Rose',
      user_id: '89',
    },
    blocks: [],
    thumbnail: '/images/media-kits/talent_profile6.jpg',
    is_public: false,
  },
  {
    id: '44',
    created_at: '2085-05-04T21:20:28.318Z',
    updated_at: '2094-08-07T21:03:47.068Z',
    name: 'Olivia Brown Future Forward',
    agency_id: 'AgencyName',
    contact: {
      manager_email: 'blah@manager.com',
      manager_name: 'Sophia Martinez',
      user_id: '89',
    },
    created_by: {
      manager_email: 'blah@manager.com',
      manager_name: 'Jett Carter',
      user_id: '89',
    },
    blocks: [],
    thumbnail: '/images/media-kits/talent_profile7.jpg',
    is_public: false,
  },
  {
    id: '49',
    created_at: '2014-08-01T06:25:28.418Z',
    updated_at: '2091-10-12T20:26:29.243Z',
    name: 'Ava Davis 2023 Initiative',
    agency_id: 'AgencyName',
    contact: {
      manager_email: 'blah@manager.com',
      manager_name: 'Sophia Martinez',
      user_id: '89',
    },
    created_by: {
      manager_email: 'blah@manager.com',
      manager_name: 'Zane Rivers',
      user_id: '89',
    },
    blocks: [],
    thumbnail: '/images/media-kits/talent_profile8.jpg',
    is_public: false,
  },
  {
    id: '50',
    created_at: '2014-08-01T06:25:28.418Z',
    updated_at: '2091-10-12T20:26:29.243Z',
    name: 'Amelia Anderson Unity Drive',
    agency_id: 'AgencyName',
    contact: {
      manager_email: 'blah@manager.com',
      manager_name: 'Sophia Martinez',
      user_id: '89',
    },
    created_by: {
      manager_email: 'blah@manager.com',
      manager_name: 'Zane Rivers',
      user_id: '89',
    },
    blocks: [],
    thumbnail: '/images/media-kits/talent_profile9.jpg',
    is_public: false,
  },
  {
    id: '51',
    created_at: '2014-08-01T06:25:28.418Z',
    updated_at: '2091-10-12T20:26:29.243Z',
    name: 'James Williams Essentials',
    agency_id: 'AgencyName',
    contact: {
      manager_email: 'blah@manager.com',
      manager_name: 'Sophia Martinez',
      user_id: '89',
    },
    created_by: {
      manager_email: 'blah@manager.com',
      manager_name: 'Phoenix Ray',
      user_id: '89',
    },
    blocks: [],
    thumbnail: '/images/media-kits/talent_profile10.jpg',
    is_public: false,
  },
  {
    id: '52',
    created_at: '2014-08-01T06:25:28.418Z',
    updated_at: '2091-10-12T20:26:29.243Z',
    name: 'Emma Johnson 2023 Vision',
    agency_id: 'AgencyName',
    contact: {
      manager_email: 'blah@manager.com',
      manager_name: 'Sophia Martinez',
      user_id: '89',
    },
    created_by: {
      manager_email: 'blah@manager.com',
      manager_name: 'Michael Brown',
      user_id: '89',
    },
    blocks: [],
    thumbnail: '/images/media-kits/talent_profile11.jpg',
    is_public: false,
  },
  {
    id: '53',
    created_at: '2014-08-01T06:25:28.418Z',
    updated_at: '2091-10-12T20:26:29.243Z',
    name: 'Mia Wilson Progress Path 2022',
    agency_id: 'AgencyName',
    contact: {
      manager_email: 'blah@manager.com',
      manager_name: 'Sophia Martinez',
      user_id: '89',
    },
    created_by: {
      manager_email: 'blah@manager.com',
      manager_name: 'manager name 1',
      user_id: '89',
    },
    blocks: [],
    thumbnail: '/images/media-kits/talent_profile12.jpg',
    is_public: false,
  },
  {
    id: '54',
    created_at: '2014-08-01T06:25:28.418Z',
    updated_at: '2091-10-12T20:26:29.243Z',
    name: 'Olivia Martinez Campaign 2024',
    agency_id: 'AgencyName',
    contact: {
      manager_email: 'blah@manager.com',
      manager_name: 'Sophia Martinez',
      user_id: '89',
    },
    created_by: {
      manager_email: 'blah@manager.com',
      manager_name: 'manager name 1',
      user_id: '89',
    },
    blocks: [],
    thumbnail: '/images/media-kits/talent_profile.jpg',
    is_public: false,
  },
];
